import { useState, useMemo } from 'react';
import moment from 'moment';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { ChevronRightIcon } from '@heroicons/react/20/solid';

// Calculate how many times a schedule will occur in a given month
const calculateOccurrencesInMonth = (schedule, monthStart, monthEnd) => {
	if (!schedule.active) return [];

	const occurrences = [];
	const scheduleStart = schedule.startDate ? moment(schedule.startDate) : moment();
	const scheduleEnd = schedule.endDate ? moment(schedule.endDate) : null;

	// Skip if schedule hasn't started yet or has ended before this month
	if (scheduleStart.isAfter(monthEnd)) return [];
	if (scheduleEnd && scheduleEnd.isBefore(monthStart)) return [];

	// For custom dates, check which fall in this month
	if (schedule.occurrences === 'custom' && schedule.customDates?.length) {
		schedule.customDates.forEach(date => {
			const d = moment(date);
			if (d.isBetween(monthStart, monthEnd, 'day', '[]')) {
				occurrences.push({
					date: d.format('YYYY-MM-DD'),
					schedule,
				});
			}
		});
		return occurrences;
	}

	// Calculate based on frequency
	let current = schedule.nextDueDate
		? moment(schedule.nextDueDate)
		: scheduleStart.clone();

	// If current is before month start, advance to first occurrence in month
	while (current.isBefore(monthStart)) {
		current = advanceByFrequency(current, schedule.occurrences);
	}

	// Find all occurrences within the month
	while (current.isSameOrBefore(monthEnd)) {
		// Check if within schedule bounds
		if (scheduleEnd && current.isAfter(scheduleEnd)) break;
		if (current.isSameOrAfter(scheduleStart)) {
			occurrences.push({
				date: current.format('YYYY-MM-DD'),
				schedule,
			});
		}
		current = advanceByFrequency(current, schedule.occurrences);
	}

	return occurrences;
};

// Advance date by frequency
const advanceByFrequency = (date, frequency) => {
	const d = date.clone();
	switch (frequency) {
		case 'daily':
			return d.add(1, 'day');
		case 'weekly':
			return d.add(1, 'week');
		case 'biweekly':
			return d.add(2, 'weeks');
		case 'monthly':
			return d.add(1, 'month');
		case 'quarterly':
			return d.add(3, 'months');
		case 'biyearly':
			return d.add(6, 'months');
		case 'yearly':
			return d.add(1, 'year');
		default:
			return d.add(1, 'month');
	}
};

// Frequency labels
const FREQUENCY_LABELS = {
	daily: 'Daily',
	weekly: 'Weekly',
	biweekly: 'Bi-weekly',
	monthly: 'Monthly',
	quarterly: 'Quarterly',
	biyearly: 'Bi-yearly',
	yearly: 'Yearly',
	custom: 'Custom',
};

// Modal component for showing estimated PMs
const EstimatedPMsModal = ({ isOpen, onClose, month, estimatedPMs, onScheduleClick }) => {
	if (!isOpen) return null;

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			<div className="flex min-h-full items-center justify-center p-4">
				{/* Backdrop */}
				<div
					className="fixed inset-0 bg-black/30"
					onClick={onClose}
				/>

				{/* Modal */}
				<div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[80vh] flex flex-col">
					{/* Header */}
					<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
						<div>
							<h3 className="text-lg font-semibold text-gray-900">
								Estimated PMs for {month}
							</h3>
							<p className="text-sm text-gray-500">
								{estimatedPMs.length} scheduled maintenance{estimatedPMs.length !== 1 ? 's' : ''}
							</p>
						</div>
						<button
							type="button"
							onClick={onClose}
							className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
						>
							<XMarkIcon className="h-5 w-5" />
						</button>
					</div>

					{/* Content */}
					<div className="flex-1 overflow-y-auto px-6 py-4">
						{estimatedPMs.length === 0 ? (
							<div className="text-center py-8">
								<p className="text-gray-500">No scheduled PMs for this month</p>
							</div>
						) : (
							<div className="space-y-3">
								{estimatedPMs.map((pm, index) => (
									<div
										key={`${pm.schedule.id}-${pm.date}-${index}`}
										className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer group"
										onClick={() => onScheduleClick?.(pm.schedule)}
									>
										<div className="flex-1 min-w-0">
											<div className="flex items-center gap-2">
												<span className="text-sm font-medium text-gray-900 truncate">
													{pm.schedule.name || 'Untitled Schedule'}
												</span>
												<span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
													{FREQUENCY_LABELS[pm.schedule.occurrences] || pm.schedule.occurrences}
												</span>
											</div>
											<div className="flex items-center gap-2 mt-1">
												<span className="text-xs text-gray-500">
													{moment(pm.date).format('MMM D, YYYY')}
												</span>
												{pm.schedule.category?.name && (
													<>
														<span className="text-xs text-gray-300">•</span>
														<span className="text-xs text-gray-500">
															{pm.schedule.category.name}
														</span>
													</>
												)}
											</div>
										</div>
										<ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-indigo-500 flex-shrink-0" />
									</div>
								))}
							</div>
						)}
					</div>

					{/* Footer */}
					<div className="px-6 py-4 border-t border-gray-200">
						<button
							type="button"
							onClick={onClose}
							className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
						>
							Close
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

// Bar Chart component
const PMScheduleBarChart = ({ schedules, onScheduleClick }) => {
	const [selectedMonth, setSelectedMonth] = useState(null);
	const [modalData, setModalData] = useState({ isOpen: false, month: '', pms: [] });

	// Calculate estimated PMs for each of the next 12 months
	const monthlyData = useMemo(() => {
		const data = [];
		const now = moment();

		for (let i = 0; i < 12; i++) {
			const monthStart = now.clone().add(i, 'months').startOf('month');
			const monthEnd = monthStart.clone().endOf('month');
			const monthLabel = monthStart.format('MMM');
			const monthYear = monthStart.format('MMMM YYYY');

			let monthPMs = [];
			(schedules || []).forEach(schedule => {
				const occurrences = calculateOccurrencesInMonth(schedule, monthStart, monthEnd);
				monthPMs = [...monthPMs, ...occurrences];
			});

			// Sort by date
			monthPMs.sort((a, b) => moment(a.date).diff(moment(b.date)));

			data.push({
				month: monthLabel,
				monthFull: monthYear,
				count: monthPMs.length,
				pms: monthPMs,
			});
		}

		return data;
	}, [schedules]);

	// Find max count for scaling
	const maxCount = useMemo(() => {
		return Math.max(...monthlyData.map(d => d.count), 1);
	}, [monthlyData]);

	const handleBarClick = (monthData) => {
		setSelectedMonth(monthData.month);
		setModalData({
			isOpen: true,
			month: monthData.monthFull,
			pms: monthData.pms,
		});
	};

	const handleCloseModal = () => {
		setModalData({ isOpen: false, month: '', pms: [] });
		setSelectedMonth(null);
	};

	return (
		<div className="mt-6">
			<h4 className="text-sm font-medium text-gray-900 mb-4">
				Estimated PMs by Month (Next 12 Months)
			</h4>

			{/* Bar Chart */}
			<div className="bg-gray-50 rounded-lg p-4">
				<div className="flex items-end justify-between gap-2 h-48">
					{monthlyData.map((data, index) => {
						const heightPercent = maxCount > 0 ? (data.count / maxCount) * 100 : 0;
						const isSelected = selectedMonth === data.month;

						return (
							<div
								key={data.month}
								className="flex-1 flex flex-col items-center gap-2"
							>
								{/* Count label */}
								<span className="text-xs font-medium text-gray-600">
									{data.count}
								</span>

								{/* Bar */}
								<div className="w-full flex-1 flex items-end">
									<button
										type="button"
										onClick={() => handleBarClick(data)}
										className={`w-full rounded-t-md transition-all duration-200 hover:opacity-80 ${
											isSelected
												? 'bg-indigo-600'
												: index === 0
													? 'bg-indigo-500'
													: 'bg-indigo-400'
										}`}
										style={{
											height: `${Math.max(heightPercent, 4)}%`,
											minHeight: data.count > 0 ? '8px' : '4px',
										}}
										title={`${data.monthFull}: ${data.count} PMs`}
									/>
								</div>

								{/* Month label */}
								<span className={`text-xs ${
									isSelected ? 'font-semibold text-indigo-600' : 'text-gray-500'
								}`}>
									{data.month}
								</span>
							</div>
						);
					})}
				</div>

				{/* Y-axis labels */}
				<div className="flex justify-between mt-2 text-xs text-gray-400">
					<span>0</span>
					<span>{Math.ceil(maxCount / 2)}</span>
					<span>{maxCount}</span>
				</div>
			</div>

			{/* Summary */}
			<div className="mt-4 flex items-center gap-4 text-sm">
				<div className="flex items-center gap-2">
					<span className="w-3 h-3 rounded bg-indigo-500"></span>
					<span className="text-gray-600">
						Total: {monthlyData.reduce((sum, d) => sum + d.count, 0)} estimated PMs
					</span>
				</div>
				<span className="text-gray-400">|</span>
				<span className="text-gray-500">
					Click on a bar to view details
				</span>
			</div>

			{/* Modal */}
			<EstimatedPMsModal
				isOpen={modalData.isOpen}
				onClose={handleCloseModal}
				month={modalData.month}
				estimatedPMs={modalData.pms}
				onScheduleClick={onScheduleClick}
			/>
		</div>
	);
};

export default PMScheduleBarChart;
